import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';

import { fetchAllTourEvents } from '@carboncopyco/data';
import type { eventWithTime } from '@carboncopyco/rrweb-types';
import type { RRWebEvent, Tour, TourFrame } from '@carboncopyco/types';
import { setCapturedScreenSize } from '../store';
import { getUncompressedEvents, processEvents } from '../utils';
import { getFirstEvent } from '../utils/replayUtils';
import { useTourDispatch } from './store';

export const useTourEvents = (
  tour: Tour,
  frames: TourFrame[],
  uuid?: string
) => {
  const { toursStyleInlining } = useFlags();
  const dispatch = useTourDispatch();
  const [tourEvents, setTourEvents] = useState<eventWithTime[]>([]);
  const [packedEventsPerFrame, setPackedEventsPerFrame] = useState<
    RRWebEvent[][]
  >([]);

  useEffect(() => {
    if (tour && frames?.length > 0) {
      fetchAllTourEvents(
        frames.map((frame) => frame.frameDataUrl).filter(Boolean),
        tour.fileServiceToken
      ).then((packedEvents) => {
        setPackedEventsPerFrame(packedEvents);
      });
    }
  }, [tour, frames?.length]);

  const tourEventsPerFrame: eventWithTime[][] = useMemo(() => {
    if (!packedEventsPerFrame?.length) {
      return [];
    }
    console.time('[perf]: unpacking compressed tourEvents');
    const uncompressedEvents = getUncompressedEvents(packedEventsPerFrame);
    console.timeEnd('[perf]: unpacking compressed tourEvents');

    return uncompressedEvents;
  }, [packedEventsPerFrame]);

  useEffect(() => {
    if (tourEventsPerFrame?.length && tour?.fileServiceToken) {
      const setTourEventsTask = async () => {
        setTourEvents(
          await processEvents(
            tourEventsPerFrame?.flat(1),
            tour.fileServiceToken,
            {
              inlineStyles: toursStyleInlining,
            }
          )
        );
      };
      setTourEventsTask().catch(console.error);
    }
  }, [tourEventsPerFrame]);

  // Set captured screen size which is used for screenshot uploading
  useEffect(() => {
    if (!tourEvents?.length || !uuid) {
      return;
    }

    const firstEvent = getFirstEvent(tourEvents);

    const width = (firstEvent.data as any).width;
    const height = (firstEvent.data as any).height;

    dispatch(
      setCapturedScreenSize({
        uuid: uuid,
        size: { width, height },
      })
    );
  }, [tourEvents]);

  return {
    tourEvents,
  };
};
